import { useEffect, useState } from "react";
import {
  OccurrenceDispatch,
  OccurrenceReview,
  OccurrenceStep,
  OccurrenceStepLog,
} from "../../../models/occurrence";
import { Occurrence } from "../../../models/occurrence";
import {
  getOccurrenceStepKeyOfValue,
  getOccurrenceStepLabelByStepValue,
  OccurrenceStepEnum,
} from "../../../enums/occurrenceStep";
import { formatMilisecondsInDaysHoursMinutes } from "../../../utils/dateUtils";
import StepFormDialog from "../dialogs/stepFormDialog";
import { fetchOccurrence } from "../../../api/fetchOccurence";
import { requiredFieldsByStep } from "./form/utils/required-fields-by-step";
import ErrorDialog from "../../../components/dialogs/ErrorDialog";
import { fieldsLabels } from "./form/utils/fields-labels";
import { Dialog, SimpleTooltip } from "@rio-cloud/rio-uikit";
import { OccurrenceTypeLabelEnum } from "../../../enums/occurrenceType";
import { Step } from "../../../models/step";
import StepReleaseForm from "./form/stepReleaseForm";
import { fetchSchedulingByChassisList } from "../../../api/fetchScheduling";
import { Scheduling } from "../../../models/schedule";
import TasNumberRequiredDialog, {
  requiresTasBeforeEntering,
} from "../dialogs/tasNumberRequiredDialog";

interface IGenericCardProps {
  occurrence: Occurrence;
  reloadOccurrenceList: (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => Promise<void>
  partners?: { id: string; name: string }[];
  summaryStep?: OccurrenceStep | null;
  step: OccurrenceStepEnum;
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  loading: boolean;
  save: (occurrence: Occurrence) => Promise<Occurrence | false>;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null>;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
  finishOccurrence: (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number) => Promise<boolean>;
  disabledAllFields?: boolean;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  review: OccurrenceReview[];
  reviewStep: (
    occurrenceId: string,
    step: OccurrenceStepEnum,
    rate: number,
    comment?: string
  ) => Promise<void>;
  getReview: (occurrenceId: string) => Promise<void>;
  occurrenceStepAvailable: string[];
  upsertDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch
  ) => Promise<{
    wasCreated: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  cancelDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
    reason: string,
    description: string
  ) => Promise<{
    wasCanceled: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  removeDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch
  ) => Promise<void>;
  steps?: Step[];
  onRefresh: () => Promise<void>;  
}

const statusStyles = (isStepElapsedHours: boolean) => ({
  isCompleted: {
    border: "border-color-success",
    bg: "bg-green-50",
    text: "text-green-700",
    ring: "border-green-200",
    cardBg: isStepElapsedHours ? 'bg-orange-50' : 'bg-white',
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  },
  isInProgress: {
    border: "border-color-highlight",
    bg: "bg-blue-50",
    text: "text-blue-700",
    ring: "border-blue-200",
    cardBg: isStepElapsedHours ? 'bg-orange-50' : 'bg-white',
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  },
  isOpen: {
    border: "border-color-dark",
    bg: "bg-gray-200",
    text: "text-gray-600",
    ring: "border-gray-300",
    cardBg: isStepElapsedHours ? 'bg-orange-50' : 'bg-transparent',
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  },
});

export const GenericCard = (props: IGenericCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [view, setView] = useState<"form" | "changeStep" | "comments" | "worklog" | "budgets" | null>(
    null
  );
  const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null
  );
  const [releaseFormDialog, setReleaseFormDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    title: "",
    body: <></>,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingTasChange, setPendingTasChange] = useState<{
    occurrence: Occurrence;
    fromStepId: number;
    toStep: OccurrenceStepEnum;
  } | null>(null);
  const [tasModalLoading, setTasModalLoading] = useState(false);

  const checkOutAt = props.summaryStep?.dtEnd;

  const [completionTime, setCompletionTime] = useState(0);
  useEffect(() => {
    const logs = props.summaryStep?.logs ?? [];
    
    if (logs.length === 0) {
      setCompletionTime(0);
      return;
    }

    const totalTime = logs.reduce((acc: number, log: OccurrenceStepLog) => {
      if (!log?.startDate) {
        return acc;
      }

      const startTime = new Date(log.startDate).getTime();
      
      if (log.endDate) {
        const endTime = new Date(log.endDate).getTime();
        return acc + (endTime - startTime);
      }
      
      const now = new Date().getTime();
      return acc + (now - startTime);
    }, 0);

    setCompletionTime(totalTime);
  }, [props.summaryStep]);

  const isStepElapsedHours = () => {
    if (props.summaryStep?.dtEnd || !props.steps) return false;
    const currentElapsedHours = parseInt(completionTime.toString())
    const stepElapsedHours = props.steps?.find((s) => s.id === props.step)?.elapsedHours ?? 0;
    return currentElapsedHours > (stepElapsedHours * 60 * 60 * 1000);
  }

  const isCompleted = checkOutAt !== undefined && checkOutAt !== null;
  const isInProgress =
    (props.summaryStep && checkOutAt === undefined) || checkOutAt === null;

  let styles = statusStyles(isStepElapsedHours()).isOpen;
  if (isCompleted) {
    styles = statusStyles(isStepElapsedHours()).isCompleted;
  } else if (isInProgress) {
    styles = statusStyles(isStepElapsedHours()).isInProgress;
  }

  const getStatusText = () => {
    if (isLoading) return "Carregando...";

    if (isHovered) {
      if (!props.summaryStep) return "Iniciar";
      if (isInProgress) return "Finalizar";
      if (isCompleted) return "Reabrir";
    }

    if (props.step === OccurrenceStepEnum.RELEASE && isCompleted && !props.occurrence.finished) {
      return "Não finalizado";
    }

    return !props.summaryStep
      ? "Não iniciado"
      : isInProgress
      ? "Em progresso"
      : "Concluído";
  };

  useEffect(() => {
    const fetchScheduling = async () => {
      const occurrence = props.occurrence;

      if (occurrence?.scheduleNumber) {
        const scheduling = (await fetchSchedulingByChassisList([occurrence.vehicleChassis])).find(
          (e) => e.scheduleNumber === occurrence.scheduleNumber,
        );
        setScheduling(scheduling);
      }
    }

    fetchScheduling()
  }, [])

  const handleStepChange = async () => {
    setIsLoading(true);

    try {
      const lastStepIndex = props.occurrenceStepAvailable.length - 1;
      const fromStep = props.occurrence.steps?.find((s) => !s.dtEnd);
      const fromStepEnum = Object.keys(OccurrenceStepEnum).find(
        (key) => OccurrenceStepEnum[key] === fromStep?.step
      ) as OccurrenceStepEnum;

        const fromOccurrence = await fetchOccurrence(
        props.occurrence.id,
        props.persona,
        props.finalized
      );
      if (!fromOccurrence) {
        setIsLoading(false);
        return;
      }

      const isPreventive = fromOccurrence.occurrenceType === "P";
      const isRoadAssistance = fromOccurrence.occurrenceType === 'A' || fromOccurrence.occurrenceType === 'W';
      const isDealership = fromOccurrence.occurrenceType === 'D';

      const currentStepEnum = fromStepEnum || props.step;
      const stepKeyFromEnum = getOccurrenceStepKeyOfValue(currentStepEnum) || currentStepEnum;
      const stepKey = `${stepKeyFromEnum}${isPreventive ? "_PREVENTIVE_CHECKUP" : ""}`;
      
      let requiredFields = [
        ...(requiredFieldsByStep[stepKey as keyof typeof requiredFieldsByStep]),
      ];

      const isCallStep = currentStepEnum === OccurrenceStepEnum.CALL ||
        stepKeyFromEnum === 'CALL' || String(stepKeyFromEnum).startsWith('CALL');
      if (isCallStep && !isPreventive) {
        if (isDealership) {
          const dealershipAllowed = [
            'vehicleOdometer', 'vehicleLicensePlate', 'vehicleModel',
            'dealershipDn', 'emissionStandard', 'country', 'source',
          ];
          requiredFields = requiredFields.filter(f => dealershipAllowed.includes(f));
        } else {
          requiredFields = requiredFields.filter((f) => {
            if (isRoadAssistance && f === 'driverName') return false;
            if (f === 'vehicleStopped') return false;
            if (f === 'occurrenceType') return false;
            return true;
          });
        }
      }

      // Add parts validation if to step is PARTS or PARTS_IN_TRANSIT
      if(props.step === OccurrenceStepEnum.PARTS) {
        requiredFields.push('partOrders');
      }
      if(props.step === OccurrenceStepEnum.PARTS_IN_TRANSIT) {
        requiredFields.push('partOrders', 'partOrderParts');
      }

      if (
        isInProgress &&
        props.step !== OccurrenceStepEnum.RELEASE &&
        OccurrenceStepEnum[
          props.occurrenceStepAvailable[
            lastStepIndex
          ] as keyof typeof OccurrenceStepEnum
        ] !== props.step
      ) {
        setView("changeStep");
        setIsLoading(false);
        return;
      }

      const currentStep = getOccurrenceStepKeyOfValue(props.step);
      if (
        props.occurrenceStepAvailable[lastStepIndex] === currentStep &&
        props.step !== OccurrenceStepEnum.RELEASE &&
        fromStepEnum === currentStep
      ) {
        const nextStepIndex =
          Object.values(OccurrenceStepEnum).indexOf(props.step) + 1;
        const nextStep = Object.values(OccurrenceStepEnum)[nextStepIndex];

        if (fromStep?.id && requiresTasBeforeEntering(nextStep, fromOccurrence)) {
          setPendingTasChange({ occurrence: fromOccurrence, fromStepId: fromStep.id, toStep: nextStep });
          setIsLoading(false);
          return;
        }

        fromStep?.id && await props.changeStep(props.occurrence, fromStep.id, nextStep);
        setIsLoading(false);
        return;
      }

      if (!fromStepEnum && props.step === OccurrenceStepEnum.CALL) {
        props.changeStep(props.occurrence, -1, props.step);
        setIsLoading(false);
        return;
      }

      const isConcludingRelease =
        isInProgress && props.step === OccurrenceStepEnum.RELEASE;

      if (!fromStepEnum) {
        setErrorDialog({
          show: true,
          title: isConcludingRelease
            ? "Erro ao concluir etapa"
            : "Erro ao iniciar etapa",
          body: (
            <div>
              <div className="mb-2">
                {isConcludingRelease
                  ? "Para concluir essa etapa, é necessário que a etapa de chamado tenha sido iniciada."
                  : "Para iniciar essa etapa, é necessário que a etapa de chamado tenha sido iniciada."}
              </div>
              <div className="mb-2">Campos obrigatórios não preenchidos:</div>
              <ul className="list-disc list-inside">
                {props
                  .getNotFilledRequiredFields(requiredFields, props.occurrence)
                  .map((field) => (
                    <li key={field}>
                      {fieldsLabels[field as keyof typeof fieldsLabels]}
                    </li>
                  ))}
              </ul>
            </div>
          ),
        });
        setIsLoading(false);
        return;
      }

    
      if (!props.isValidStep(requiredFields, fromOccurrence)) {
        setErrorDialog({
          show: true,
          title: isConcludingRelease
            ? "Erro ao concluir etapa"
            : "Erro ao iniciar etapa",
          body: (
            <div>
              <div className="mb-2">
                {isConcludingRelease
                  ? "É necessário preencher todos os campos obrigatórios da etapa em progresso antes de concluir."
                  : "É necessário preencher todos os campos obrigatórios da etapa em progresso antes de avançar."}
              </div>
              <div className="mb-2">Campos obrigatórios não preenchidos:</div>
              <ul className="list-disc list-inside">
                {props
                  .getNotFilledRequiredFields(requiredFields, fromOccurrence)
                  .map((field) => (
                    <li key={field}>
                      {fieldsLabels[field as keyof typeof fieldsLabels]}
                    </li>
                  ))}
              </ul>
            </div>
          ),
        });
        setIsLoading(false);
        return;
      }

      if (isInProgress && props.step === OccurrenceStepEnum.RELEASE) {
        await props.finishOccurrence(props.occurrence, undefined, undefined, fromStep?.id);

        if(props.occurrence?.volksEssentialsPlanContract) {
          setReleaseFormDialog(true)
          return;
        }

        props.reloadOccurrenceList(props?.occurrence?.id)

      } else if (fromStep?.id && requiresTasBeforeEntering(props.step, fromOccurrence)) {
        setPendingTasChange({ occurrence: fromOccurrence, fromStepId: fromStep.id, toStep: props.step });
        setIsLoading(false);
        return;
      } else {
        fromStep?.id && await props.changeStep(props.occurrence, fromStep.id, props.step);
      }
    } catch (error: any) {
      console.error("Erro na API:", error);
      
      const errorCode = error?.code || error?.response?.data?.code;
      const errorMessage = error?.message || error?.response?.data?.message;
      
      if (errorCode === "47" || errorCode === 47) {
        setErrorDialog({
          show: true,
          title: "Etapa alterada por outro usuário",
          body: (
            <div>
            Para continuar, é necessário atualizar as informações dos cards.
            </div>
          ),
        });
      } else if (errorMessage) {
        setErrorDialog({
          show: true,
          title: "Erro ao alterar etapa",
          body: <div>{errorMessage}</div>,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className="p-[10px]"
        style={{ backgroundColor: "#C7D1D9", height: "307px", boxSizing: "border-box", width: "340px" }}
      >
        <div
          key={props.occurrence.id}
          className={`w-[320px] border  ${
            props.summaryStep && !props.occurrence.finished ? "cursor-pointer" : ""
          } ${styles.border} rounded-lg p-4 flex flex-col justify-between  ${
            styles.cardBg
          } text-size-14 text-gray-800 ${isStepElapsedHours() ? "!bg-orange-50" : ""}`}
          style={{ height: "285px", borderWidth: "1.7px" }}
          onClick={() => {
            props.summaryStep && setView("form");
            props.getReview(props.occurrence.id);
          }}
        >
          <div>
            <div className="flex justify-between">
              <span>Início:</span>
              <span>
                {(props.summaryStep?.logs?.[0]?.startDate ?? props.summaryStep?.dtStart)?.toLocaleString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }) ?? "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Conclusão:</span>
              <span>
                {props.summaryStep?.dtEnd?.toLocaleString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }) ?? "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Tempo estimado:</span>
              <div className="flex items-center gap-1 text-gray-500">
                <span className="rioglyph rioglyph-time-alt" />
                <span>{props.summaryStep?.estimatedTime ?? "-"}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span>Tempo corrido:</span>
              <div className="flex items-center gap-1 font-bold">
                <span className="rioglyph rioglyph-time-alt" />
                <span>
                  {completionTime
                    ? formatMilisecondsInDaysHoursMinutes(
                        parseInt(completionTime.toString())
                      )
                    : "-"}
                </span>
              </div>
            </div>
            {OccurrenceTypeLabelEnum[props.occurrence.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum] && (
              <>
                <div className="flex justify-between mb-2">
                  <span className="text-md text-gray-600">Tipo:</span>
                  <span className="text-md font-medium">
                    {OccurrenceTypeLabelEnum[props.occurrence.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum]}
                  </span>
                </div>
              </>
            )}
          </div>

          {isStepElapsedHours() && (
            <div className="flex justify-end">
              <SimpleTooltip content="Tempo excedido" placement="top">
                <span className="rioglyph rioglyph-warning-sign text-yellow-600 text-size-h5" />
              </SimpleTooltip>
            </div>
          )}

          <button
            className={`
              !mt-8 w-full text-center py-1.5 rounded-full font-medium 
              ${!isLoading && styles.bg} ${!isLoading && styles.text} ${!isLoading && styles.ring} 
              ${
                props.persona === "MANAGER" || props.finalized
                  ? ""
                  : styles.hoverBg
              } 
              ${
                props.persona === "MANAGER" || props.finalized
                  ? ""
                  : styles.hoverText
              } 
              transition-colors duration-200 
              ${isStepElapsedHours() ? "!bg-orange-100 !text-orange-700 hover:!bg-orange-200 hover:!text-orange-700" : ""}
              ${isLoading ? "btn btn-loading" : ""}
            `}
            onMouseEnter={() =>
              props.persona !== "MANAGER" &&
              !props.finalized &&
              setIsHovered(true)
            }
            onMouseLeave={() =>
              props.persona !== "MANAGER" &&
              !props.finalized &&
              setIsHovered(false)
            }
            disabled={props.occurrence.finished}
            onClick={(e) => {
              if (isLoading || props.persona === "MANAGER" || props.finalized)
                return;

              e.stopPropagation();

              setOnConfirmAction(() => () => handleStepChange());
              setShowConfirmDialog(true);
            }}
          >
            {getStatusText()}
          </button>
        </div>
      </div>

      {props.step && (
        <StepFormDialog
          reloadOccurrenceList={props.reloadOccurrenceList}
          showDialog={!!view}
          onClose={() => setView(null)}
          occurrence={props.occurrence}
          partners={props.partners}
          summaryStep={props.summaryStep}
          step={props.step}
          isValidStep={props.isValidStep}
          loading={props.loading}
          save={props.save}
          disabledAllFields={
            !!props.summaryStep?.dtEnd || props.disabledAllFields
          }
          changeStep={props.changeStep}
          view={view as "form" | "changeStep" | "comments" | "worklog" | "budgets"}
          setView={setView}
          getNotFilledRequiredFields={props.getNotFilledRequiredFields}
          finishOccurrence={props.finishOccurrence}
          persona={props.persona}
          finalized={props.finalized}
          review={props.review}
          reviewStep={props.reviewStep}
          occurrenceStepAvailable={props.occurrenceStepAvailable}
          upsertDispatch={props.upsertDispatch}
          cancelDispatch={props.cancelDispatch}
          removeDispatch={props.removeDispatch}
        />
      )}

      <ErrorDialog
        show={errorDialog.show}
        title={errorDialog.title}
        body={errorDialog.body}
        onClose={async () => {
          setErrorDialog({ show: false, title: "", body: <></> });
          if (errorDialog.title === "Etapa alterada por outro usuário") {
            setIsRefreshing(true);
            try {
              await props.onRefresh();
            } finally {
              setIsRefreshing(false);
            }
          }
        }}
      />

      <Dialog
          show={releaseFormDialog}
          title={'Liberação'}
          body={
            <StepReleaseForm
              scheduling={scheduling} 
              occurrence={props.occurrence} 
              setReleaseFormDialog={setReleaseFormDialog}
              closeFirstModal={() => props.reloadOccurrenceList(props?.occurrence?.id)}
            />
          }
          className='exampleDialog'
          showCloseButton={false}
          bsSize='md'
      />

      <Dialog
        show={showConfirmDialog}
        title="Confirmação"
        body={
          <div>
            {isCompleted && (
              <>
                Deseja realmente <strong>reabrir</strong> a etapa{" "}
                <strong>{getOccurrenceStepLabelByStepValue(props.step)}</strong>
                ?
              </>
            )}
            {isInProgress && !isCompleted && (
              <>
                Deseja realmente <strong>avançar</strong> de etapa{" "}
                <strong>{getOccurrenceStepLabelByStepValue(props.step)}</strong>
                ?
              </>
            )}
            {!props.summaryStep && (
              <>
                Deseja realmente <strong>iniciar</strong> a etapa{" "}
                <strong>{getOccurrenceStepLabelByStepValue(props.step)}</strong>
                ?
              </>
            )}
          </div>
        }
        footer={
          <div className="display-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                setShowConfirmDialog(false);
                onConfirmAction?.();
              }}
            >
              Confirmar
            </button>
          </div>
        }
        bsSize={Dialog.SIZE_XS}
        onClose={() => setShowConfirmDialog(false)}
      />

      <TasNumberRequiredDialog
        show={!!pendingTasChange}
        occurrence={pendingTasChange?.occurrence ?? null}
        loading={tasModalLoading}
        onCancel={() => setPendingTasChange(null)}
        onConfirm={async (tasNumber) => {
          if (!pendingTasChange) return;

          setTasModalLoading(true);
          try {
            const saved = await props.save({ ...pendingTasChange.occurrence, tasNumber });
            if (!saved) {
              setTasModalLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao salvar número TAS",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            await props.changeStep(
              { ...pendingTasChange.occurrence, tasNumber },
              pendingTasChange.fromStepId,
              pendingTasChange.toStep
            );
            setTasModalLoading(false);
            setPendingTasChange(null);
          } catch (error: any) {
            setTasModalLoading(false);
            setErrorDialog({
              show: true,
              title: "Erro ao alterar etapa",
              body: <div>{error?.message ?? "Verifique os dados e tente novamente ou entre em contato com o suporte."}</div>,
            });
          }
        }}
      />
    </>
  );
};

export const SkeletonGenericCard = (props: { index: number }) => {
  const styles = {
    border: "border-color-darkest",
    bg: "bg-gray-200",
    text: "text-gray-600",
    ring: "border-gray-300",
    cardBg: "bg-transparent",
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  };

  return (
    <div className="p-[10px]" style={{ backgroundColor: "#C7D1D9", height: "307px", boxSizing: "border-box", width: "340px" }}>
      <div
        key={props.index}
        className={`w-[320px] border ${styles.border} rounded-lg p-4 ${styles.cardBg} text-size-14 text-gray-800`}
        style={{ height: "285px" }}
      >
        <div>
          <div className="flex justify-between">
            <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
          </div>
          <div className="flex justify-between">
            <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
          </div>
          <div className="flex justify-between items-center">
            <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            <div className="flex items-center gap-1 text-gray-500">
              <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            <div className="flex items-center gap-1 font-bold">
              <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
          </div>
        </div>

        <div className="!mt-8 w-full h-[28px] animate-pulse rounded-full bg-gray-200 mb-2"></div>
      </div>
    </div>
  );
};
